import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { HomeReviews } from "../components/PlayerReviews";
import WalletHeroCard from "../components/WalletHeroCard";
import IdentityVerificationPrompt from "../components/IdentityVerificationPrompt";
import PlinkoHomeGame from "../components/PlinkoHomeGame";
import CrashHomeGame from "../components/CrashHomeGame";
import { Sparkles, Trophy, ShieldCheck, Zap, Crown, Users, WalletCards, Banknote, Download } from "lucide-react";
import { siteConfig } from "../lib/siteConfig";
import { VerifiedName } from "../components/VerifiedBadge";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/f281174e-d5b3-4f4a-816c-f5cd8faf2e5b/images/f1ef492cf8817373a59291f0b591720b32024852b5a1267c3b274a73031044fe.png";

export default function Landing() {
  const { user, refreshUser } = useAuth();
  const [games, setGames] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [socialProof, setSocialProof] = useState({ stats: {}, recent_cashouts: [] });
  const [liveWinOffset, setLiveWinOffset] = useState(0);
  const isAuthed = user && user !== false;
  const primaryCta = isAuthed
    ? { to: "/dashboard", label: "Open your dashboard", testId: "hero-dashboard-button" }
    : { to: "/signup", label: "Create your account", testId: "hero-signup-button" };

  useEffect(() => {
    api.get("/games").then(({ data }) => setGames(data.filter((g) => g.status === "live").slice(0, 3))).catch(() => {});
    api.get("/leaderboard").then(({ data }) => setLeaders(data.slice(0, 6))).catch(() => {});
    api.get("/public/social-proof").then(({ data }) => setSocialProof(data || { stats: {}, recent_cashouts: [] })).catch(() => {});
  }, []);

  const proofStats = socialProof?.stats || {};
  const cashoutTicker = useMemo(() => buildCashoutTicker(socialProof?.recent_cashouts || []), [socialProof?.recent_cashouts]);
  const visibleCashouts = tickerWindow(cashoutTicker, liveWinOffset, 4);

  useEffect(() => {
    if (cashoutTicker.length <= 4) return undefined;
    const timer = window.setInterval(() => {
      setLiveWinOffset((current) => (current + 1) % cashoutTicker.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, [cashoutTicker.length]);

  return (
    <div className="min-h-screen overflow-x-clip bg-white text-slate-950 dark:bg-[#06111F] dark:text-white">
      <Navbar theme="light" />

      <section className="relative overflow-hidden border-b border-slate-200 bg-white dark:border-white/10 dark:bg-[#06111F]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-[0.06] dark:opacity-[0.12]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-white dark:from-[#06111F] dark:via-[#06111F]/95 dark:to-[#06111F]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-12 sm:px-6 sm:pb-28 sm:pt-16 md:px-10 md:pb-32 md:pt-24">
          <div className="grid min-w-0 items-start gap-10 md:grid-cols-12 lg:gap-12">
            <div className="min-w-0 md:col-span-7">
              <div className="mb-6 inline-flex max-w-full items-center gap-2 rounded-full border border-[#2F6FA5]/25 bg-[#2F6FA5]/10 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#2F6FA5] backdrop-blur fade-up sm:mb-8 sm:text-xs" data-testid="hero-badge">
                <Sparkles className="h-3.5 w-3.5" /> Now in early access - 2026
              </div>
              <h1 className="hero-shine max-w-full break-words font-display text-4xl font-black leading-[1.02] tracking-tight fade-up sm:text-5xl md:text-7xl md:leading-[0.95] lg:text-8xl">
                Free to play.<br />
                Real rewards.<br />
                One account.
              </h1>
              <p className="mt-6 max-w-full text-base leading-relaxed text-slate-600 dark:text-slate-300 fade-up sm:mt-8 sm:max-w-xl sm:text-lg md:text-xl" style={{ animationDelay: "120ms" }}>
                Spin and win across slots, poker, fishing and more. Free to play, with real prizes through our sweepstakes model.
              </p>
              <div className="mt-8 flex flex-col gap-3 fade-up sm:mt-10 sm:flex-row sm:flex-wrap" style={{ animationDelay: "240ms" }}>
                <Link to={primaryCta.to} data-testid={primaryCta.testId} className="inline-flex w-full items-center justify-center rounded-full bg-[#2F6FA5] px-6 py-4 text-center font-bold text-white shadow-xl shadow-[#2F6FA5]/25 transition-all hover:-translate-y-1 hover:bg-[#4E91C8] sm:w-auto sm:px-8">
                  {primaryCta.label}
                </Link>
                <a href={siteConfig.androidAppDownloadUrl} download="FEA-Slots.apk" data-testid="hero-download-app-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2F6FA5] px-6 py-4 text-center font-bold text-white shadow-xl shadow-[#2F6FA5]/20 transition-all hover:-translate-y-1 hover:bg-[#4E91C8] sm:w-auto sm:px-8">
                  <Download className="h-4 w-4" />
                  Download app
                </a>
                <Link to="/games" data-testid="hero-explore-button" className="inline-flex w-full items-center justify-center rounded-full border border-slate-300 px-6 py-4 text-center font-semibold text-slate-800 transition-all hover:bg-slate-100 dark:border-slate-600 dark:text-white dark:hover:bg-white/10 sm:w-auto sm:px-8">
                  Explore games ->
                </Link>
              </div>

              <div className="mt-10 grid gap-3 text-sm font-medium text-slate-600 dark:text-slate-300 fade-up min-[380px]:grid-cols-2 sm:mt-12 sm:flex sm:flex-wrap sm:gap-x-10 sm:gap-y-4" style={{ animationDelay: "360ms" }}>
                <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-[#2F6FA5]" /> Instant bonuses</span>
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#2F6FA5]" /> Host-verified</span>
                <span className="flex items-center gap-2"><Trophy className="h-4 w-4 text-[#2F6FA5]" /> Weekly leaderboards</span>
              </div>

              <div className="mt-7 grid gap-3 fade-up sm:grid-cols-3" style={{ animationDelay: "420ms" }} data-testid="public-proof-stats">
                <ProofStat icon={Users} label="Players" value={formatCount(proofStats.members, "1,000+")} />
                <ProofStat icon={WalletCards} label="Cashouts paid" value={formatStatMoney(proofStats.cashout_total, "$25K+")} />
                <ProofStat icon={Banknote} label="Payouts" value={formatCount(proofStats.cashout_count, "500+")} />
              </div>
            </div>

            <aside className="min-w-0 md:col-span-5 md:mt-4 fade-up" style={{ animationDelay: "180ms" }} data-testid="hero-leaderboard">
              <div className="max-w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/80 backdrop-blur-2xl sm:p-7">
                <div className="mb-5 flex min-w-0 items-center justify-between gap-3 sm:mb-6">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Crown className="h-5 w-5 text-[#2F6FA5]" />
                    <span className="min-w-0 truncate text-xs font-semibold uppercase tracking-[0.2em] text-[#2F6FA5] sm:text-sm sm:tracking-[0.25em]">Top players</span>
                  </div>
                  <Link to="/leaderboard" className="shrink-0 text-xs text-slate-500 hover:text-slate-950 sm:text-sm" data-testid="hero-leaderboard-see-all">
                    See all ->
                  </Link>
                </div>

                {leaders.length === 0 && (
                  <div className="py-12 text-center text-base text-slate-500">
                    No players yet - be the first to spin.
                  </div>
                )}

                <ul className="space-y-2.5">
                  {leaders.map((u, i) => (
                    <li
                      key={u.username + i}
                      data-testid={`hero-leader-${i}`}
                      className={`flex min-w-0 items-center justify-between gap-3 rounded-xl border px-3 py-3 transition-colors sm:px-4 sm:py-3.5 ${
                        i === 0
                          ? "border-[#2F6FA5]/30 bg-gradient-to-r from-[#2F6FA5]/15 to-white"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                        <span className={`w-8 shrink-0 text-center font-display text-xl font-black sm:w-10 sm:text-2xl ${i < 3 ? "gold-text" : "text-slate-300"}`}>
                          #{i + 1}
                        </span>
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#2F6FA5]/30 bg-gradient-to-br from-[#E7F0F8] to-white font-display text-sm font-bold text-[#123A5F] sm:h-11 sm:w-11 sm:text-base">
                          {u.username.charAt(0).toUpperCase()}
                        </div>
                        <VerifiedName className="text-sm font-semibold sm:text-base" name={u.username} user={u} />
                      </div>
                      <span className="shrink-0 text-right">
                        <span className="block tabular-nums text-sm font-bold text-slate-950 sm:text-base">{leaderMoney(u)}</span>
                        <span className="block text-[10px] uppercase tracking-[0.16em] text-slate-400">weekly</span>
                        {u.reward && (
                          <span className="mt-1 inline-flex rounded-full border border-[#2F6FA5]/40 bg-[#2F6FA5]/10 px-2 py-0.5 text-[10px] font-black text-[#2F6FA5]">
                            {u.reward.label}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {leaders.length > 0 && (
                  <div className="mt-6 flex min-w-0 items-center justify-between gap-3 border-t border-slate-200 pt-5 text-xs text-slate-500 sm:text-sm">
                    <span>Live - updated each spin</span>
                    <Link to={isAuthed ? "/leaderboard" : "/signup"} className="font-semibold text-[#2F6FA5] hover:underline">
                      {isAuthed ? "View board ->" : "Join the board ->"}
                    </Link>
                  </div>
                )}

                <div className="mt-6 rounded-2xl border border-[#2F6FA5]/15 bg-[#F2F8FD] p-4" data-testid="recent-real-cashouts">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="text-xs font-black uppercase tracking-[0.18em] text-[#2F6FA5]">Live wins</span>
                    <span className="rounded-full bg-white px-2 py-1 text-[10px] font-bold text-slate-500">Cashouts</span>
                  </div>
                  <ul className="space-y-2">
                    {visibleCashouts.map((row, i) => (
                      <li key={`${row.name}-${row.amount}-${liveWinOffset}-${i}`} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-sm transition-all duration-300">
                        <span className="min-w-0 truncate font-semibold text-slate-700">{row.name}</span>
                        <span className="shrink-0 font-black text-[#2F6FA5]">${Number(row.amount || 0).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {isAuthed && (
        <section className="relative z-10 mx-auto max-w-7xl space-y-5 px-6 pb-12 md:px-10 md:-mt-10">
          <WalletHeroCard user={user} onBalanceChange={() => refreshUser?.()} />
          <IdentityVerificationPrompt user={user} onUpdated={() => refreshUser?.()} />
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:px-10 md:py-12">
        <PlinkoHomeGame dark compact />
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 md:px-10 md:pb-12">
        <CrashHomeGame dark compact />
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 md:px-10 md:py-24">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4 md:mb-10">
          <div>
            <div className="mb-3 text-xs uppercase tracking-[0.2em] text-[#2F6FA5]">Featured slots</div>
            <h2 className="font-display text-3xl font-bold tracking-tight md:text-5xl">Spin the favorite slots</h2>
          </div>
          <Link to="/games" className="text-sm font-medium text-slate-500 hover:text-[#2F6FA5]" data-testid="featured-see-all">See all games -></Link>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 md:gap-6">
          {games.map((g, i) => (
            <Link
              key={g.id}
              to={`/games/${g.id}`}
              data-testid={`featured-game-${i}`}
              className="group relative flex aspect-[5/4] min-h-[9.5rem] flex-col justify-end overflow-hidden rounded-2xl border border-slate-200 bg-white text-white shadow-sm md:aspect-[4/5]"
            >
              <img src={g.image_url} alt={g.name} className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105 md:group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
              <div className="relative p-3 sm:p-4 md:p-6">
                <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-[#B7D7F2] md:mb-2 md:text-xs">{g.category}</div>
                <h3 className="font-display text-lg font-bold leading-tight md:mb-1 md:text-2xl">{g.name}</h3>
                <p className="mt-1 hidden text-sm text-white/60 line-clamp-2 sm:block">{g.description}</p>
                <div className="mt-2 inline-flex items-center gap-2 text-xs font-medium text-[#B7D7F2] transition-all group-hover:gap-3 md:mt-4 md:text-sm">
                  Play now ->
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl border-t border-slate-200 px-6 py-20 md:px-10 md:py-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="mb-3 text-xs uppercase tracking-[0.2em] text-[#2F6FA5]">How {siteConfig.name} works</div>
            <h2 className="font-display text-4xl font-bold leading-tight tracking-tight md:text-5xl">Three steps. Real prizes.</h2>
            <p className="mt-6 leading-relaxed text-slate-600">Our sweepstakes model means you play for free with dollar balances, and the rewards are real. No purchase necessary.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3 md:col-span-7">
            {[
              { n: "01", t: isAuthed ? "Choose a game" : "Create your account", d: isAuthed ? "Pick your favorite game and manage play from one account." : "Sign up free in 30 seconds - no credit card needed." },
              { n: "02", t: "Verify your phone", d: "Confirm your number to unlock loading dollars and playing games." },
              { n: "03", t: "Play & redeem", d: "Spin slots, climb the board, redeem real-world rewards." },
            ].map((s) => (
              <div key={s.n} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-3 font-display text-3xl gold-text">{s.n}</div>
                <h4 className="mb-2 font-semibold">{s.t}</h4>
                <p className="text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-10">
        <div className="relative overflow-hidden rounded-3xl border border-[#2F6FA5]/25 bg-white p-12 text-center text-slate-950 shadow-sm md:p-20">
          <h2 className="font-display text-4xl font-black tracking-tighter md:text-6xl">
            Your reels are <span className="gold-text">waiting.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-slate-600">
            {isAuthed ? "Your account is ready. Jump back into the games and keep your play moving." : "Free to play. Real to win. Join thousands already spinning the FEA reels."}
          </p>
          <Link to={isAuthed ? "/games" : "/signup"} data-testid={isAuthed ? "cta-games-button" : "cta-signup-button"} className="mt-10 inline-block rounded-full bg-[#2F6FA5] px-10 py-4 font-bold text-white transition-all hover:-translate-y-1 hover:bg-[#4E91C8]">
            {isAuthed ? "Browse games" : "Start playing - it is free"}
          </Link>
        </div>
      </section>

      <HomeReviews />
      <Footer theme="light" />
    </div>
  );
}

function leaderMoney(row) {
  const coins = Number(row?.total_load_coins ?? row?.coins ?? 0);
  return `$${(coins / 1000).toFixed(2)}`;
}

function ProofStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-[#2F6FA5]/15 bg-white/80 p-4 shadow-sm backdrop-blur dark:bg-white/5">
      <Icon className="mb-2 h-5 w-5 text-[#2F6FA5]" />
      <div className="font-display text-2xl font-black text-slate-950 dark:text-white">{value}</div>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}

function formatCount(value, fallback) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  return numeric.toLocaleString();
}

function formatStatMoney(value, fallback) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  if (numeric >= 1000) return `$${(numeric / 1000).toFixed(numeric >= 10000 ? 0 : 1)}K`;
  return `$${numeric.toFixed(0)}`;
}

function buildCashoutTicker(realRows) {
  const real = (realRows || [])
    .filter((row) => Number(row?.amount || 0) > 0)
    .map((row) => ({
      name: row.name || "Player",
      amount: Number(row.amount || 0),
    }));
  const merged = [];
  const maxLength = Math.max(12, real.length + DISPLAY_CASHOUTS.length);
  for (let i = 0; i < maxLength; i += 1) {
    if (real[i]) merged.push(real[i]);
    const filler = DISPLAY_CASHOUTS[i % DISPLAY_CASHOUTS.length];
    if (filler) merged.push(filler);
  }
  return merged.slice(0, 18);
}

function tickerWindow(rows, offset, size) {
  if (!rows.length) return DISPLAY_CASHOUTS.slice(0, size);
  return Array.from({ length: Math.min(size, rows.length) }, (_, i) => rows[(offset + i) % rows.length]);
}

const DISPLAY_CASHOUTS = [
  { name: "Shaxxxia", amount: 700 },
  { name: "Milxxx77", amount: 425 },
  { name: "Jayxxxrd", amount: 315 },
  { name: "Nixxx84", amount: 250 },
  { name: "Kevxxxer", amount: 180 },
  { name: "Zacxxxdy", amount: 95 },
  { name: "Melxxxsa", amount: 60 },
  { name: "Robxxx12", amount: 40 },
];
